import { Expo, gsap } from "gsap";
import { useEffect, useState } from "react";

import { TypedArrayRefObject, TypedRefObject } from "@/types/interactivity";

import { Spacing } from "@/tokens/spacing";

import { useIsomorphicLayoutEffect } from "@/util/hooks/effect_hooks";

export function useSidebarLinksAnimation({
    buttonRef,
    containerRef,
    iconRef,
    isExpanded,
    itemsRef,
}: {
    buttonRef: TypedRefObject;
    containerRef: TypedRefObject;
    iconRef: TypedRefObject;
    isExpanded: boolean;
    itemsRef: TypedArrayRefObject;
}) {
    /**
     * State Management
     */
    const [didInit, setDidInit] = useState(true);

    /**
     * Effects
     */
    useIsomorphicLayoutEffect(() => {
        if (!didInit) {
            gsap.set(containerRef.current, {
                height: "auto",
            });

            gsap.set(itemsRef.current, {
                autoAlpha: 1,
                onComplete: () => {
                    setDidInit(true);
                },
            });
        }
    }, [containerRef, itemsRef, didInit]);

    useEffect(() => {
        if (didInit) {
            const tl = gsap.timeline();

            if (!isExpanded) {
                tl.to(iconRef.current, {
                    duration: 0.15,
                    ease: Expo.easeOut,
                    rotate: 0,
                });

                tl.to(
                    buttonRef.current,
                    {
                        duration: 0.25,
                        marginBottom: 0,
                    },
                    "<",
                );

                tl.to(
                    itemsRef.current,
                    {
                        autoAlpha: 0,
                        duration: 0.1,
                        stagger: 0.1,
                    },
                    "<",
                );

                tl.to(
                    containerRef.current,
                    {
                        duration: 0.25,
                        height: 0,
                    },
                    "<",
                );
            } else {
                tl.to(iconRef.current, {
                    duration: 0.15,
                    ease: Expo.easeInOut,
                    rotate: 90,
                });

                tl.to(
                    buttonRef.current,
                    {
                        duration: 0.25,
                        marginBottom: Spacing["spacing-4"],
                    },
                    "<",
                );

                tl.to(
                    containerRef.current,
                    {
                        duration: 0.25,
                        height: "auto",
                    },
                    "<",
                );

                tl.to(
                    itemsRef.current,
                    {
                        autoAlpha: 1,
                        duration: 0,
                    },
                    "<",
                );
            }

            return () => {
                tl.kill();
            };
        }
    }, [containerRef, itemsRef, isExpanded, iconRef, buttonRef, didInit]);
}
